import { useState, useMemo } from 'react';
import { ClipboardPaste, ArrowRight, ArrowLeft, Check, AlertCircle, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import { formatNumber, formatPercent } from '../lib/utils';

// ─── Constants ────────────────────────────────────────────

const TARGET_FIELDS = [
  { value: '_skip', label: 'Skip' },
  { value: 'name', label: 'Name' },
  { value: 'category', label: 'Category' },
  { value: 'qty', label: 'Qty' },
  { value: 'revenue_unit', label: 'Rev/Unit' },
  { value: 'revenue_currency', label: 'Rev Currency' },
  { value: 'cost_unit', label: 'Cost/Unit' },
  { value: 'cost_currency', label: 'Cost Currency' },
  { value: 'vendor', label: 'Vendor' },
];

const HEADER_ALIASES = {
  name: 'name', line_name: 'name', item_name: 'name', line: 'name', item: 'name', description: 'name',
  category: 'category', cat: 'category',
  qty: 'qty', quantity: 'qty',
  revenue_unit: 'revenue_unit', rev_unit: 'revenue_unit', price: 'revenue_unit', unit_price: 'revenue_unit', revenue: 'revenue_unit', rev: 'revenue_unit', sell: 'revenue_unit', sell_price: 'revenue_unit',
  revenue_currency: 'revenue_currency', rev_currency: 'revenue_currency', rev_cur: 'revenue_currency',
  cost_unit: 'cost_unit', cost: 'cost_unit', unit_cost: 'cost_unit', buy: 'cost_unit', buy_price: 'cost_unit',
  cost_currency: 'cost_currency', cost_cur: 'cost_currency',
  vendor: 'vendor', supplier: 'vendor',
};

const VALID_CURRENCIES = ['CHF', 'EUR', 'USD'];

// ─── Parsing Helpers ──────────────────────────────────────

function detectDelimiter(text) {
  const firstLine = text.split('\n')[0] || '';
  const tabs = (firstLine.match(/\t/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return tabs >= commas ? '\t' : ',';
}

function parseClipboard(text) {
  const delimiter = detectDelimiter(text);
  const lines = text.split('\n').filter(l => l.trim());
  const rows = lines.map(l => l.split(delimiter).map(c => c.trim()));
  const maxCols = Math.max(...rows.map(r => r.length));
  return rows.map(r => {
    while (r.length < maxCols) r.push('');
    return r;
  });
}

function detectHeaders(firstRow) {
  const mapped = firstRow.map(h => HEADER_ALIASES[h.toLowerCase().replace(/[\s\-]+/g, '_')] || null);
  return mapped.filter(Boolean).length >= 2 ? mapped : null;
}

function parseNum(str) {
  if (str === null || str === undefined || str === '') return null;
  const cleaned = String(str).replace(/['\u2019\s]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

function toChfPreview(value, currency, fxRates, baseCurrency = 'CHF') {
  if (!fxRates) return value;
  // Convert source currency -> CHF -> base
  const toChf = (amt, cur) => {
    if (cur === 'CHF') return amt;
    if (cur === 'EUR') return amt * (fxRates.EUR_CHF || 1);
    if (cur === 'USD') return amt * (fxRates.USD_CHF || 1);
    return amt;
  };
  const fromChf = (amt, target) => {
    if (target === 'CHF') return amt;
    if (target === 'EUR') return amt / (fxRates.EUR_CHF || 1);
    if (target === 'USD') return amt / (fxRates.USD_CHF || 1);
    return amt;
  };
  return fromChf(toChf(value, currency), baseCurrency);
}

// ─── Validation ───────────────────────────────────────────

function validateCell(value, field) {
  const str = String(value ?? '').trim();
  if (!str) return null;
  switch (field) {
    case 'qty':
    case 'revenue_unit':
    case 'cost_unit': {
      const n = parseNum(str);
      if (n === null) return 'Not a number';
      if (field === 'qty' && n < 0) return 'Must be >= 0';
      return null;
    }
    case 'revenue_currency':
    case 'cost_currency':
      return VALID_CURRENCIES.includes(str.toUpperCase()) ? null : 'Invalid currency';
    default:
      return null;
  }
}

// ─── Component ────────────────────────────────────────────

export function PasteImportDialog({ open, onOpenChange, onImport, fxRates, existingItemCount, coreCurrency = 'CHF', loading }) {
  const [step, setStep] = useState('paste');
  const [rawText, setRawText] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [hasHeaders, setHasHeaders] = useState(false);
  const [columnMap, setColumnMap] = useState({});
  const [mode, setMode] = useState('create');
  const [emptyCategoryAck, setEmptyCategoryAck] = useState('');
  const [suspiciousNameAck, setSuspiciousNameAck] = useState(false);

  const reset = () => {
    setStep('paste');
    setRawText('');
    setParsedRows([]);
    setHasHeaders(false);
    setColumnMap({});
    setMode('create');
    setEmptyCategoryAck('');
    setSuspiciousNameAck(false);
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  // ── Parse ──
  const handleParse = () => {
    const rows = parseClipboard(rawText);
    if (rows.length === 0) return;
    setParsedRows(rows);

    const headerMap = detectHeaders(rows[0]);
    if (headerMap) {
      setHasHeaders(true);
      const map = {};
      headerMap.forEach((field, idx) => { if (field) map[idx] = field; });
      setColumnMap(map);
    } else {
      setHasHeaders(false);
      setColumnMap({});
    }
    setStep('map');
  };

  // ── Derived data ──
  const dataRows = hasHeaders ? parsedRows.slice(1) : parsedRows;
  const headerLabels = hasHeaders ? parsedRows[0] : parsedRows[0]?.map((_, i) => `Col ${i + 1}`) || [];
  const colCount = headerLabels.length;

  const getMapped = (i) => columnMap[i] || '_skip';

  const setMapping = (colIdx, field) => {
    setColumnMap(prev => {
      const next = { ...prev };
      if (field === '_skip') { delete next[colIdx]; return next; }
      // Remove duplicate mapping
      Object.keys(next).forEach(k => { if (next[k] === field && Number(k) !== colIdx) delete next[k]; });
      next[colIdx] = field;
      return next;
    });
    // Reset acknowledgements when mapping changes
    setEmptyCategoryAck('');
    setSuspiciousNameAck(false);
  };

  // ── Schema validation ──
  const mappedFields = useMemo(() => new Set(Object.values(columnMap)), [columnMap]);
  const nameIsMapped = mappedFields.has('name');
  const categoryIsMapped = mappedFields.has('category');

  // Suspicious name detection: if the "name" column has very few unique values
  // relative to row count, it's likely a category column mapped wrong
  const suspiciousNameMapping = useMemo(() => {
    if (!nameIsMapped || dataRows.length < 4) return false;
    const nameColIdx = Number(Object.keys(columnMap).find(k => columnMap[k] === 'name'));
    if (isNaN(nameColIdx)) return false;
    const nameValues = dataRows.map(r => (r[nameColIdx] || '').trim()).filter(Boolean);
    const uniqueNames = new Set(nameValues);
    // If ≤3 unique values across 4+ rows, this is suspicious
    return uniqueNames.size <= 3 && nameValues.length >= 4;
  }, [columnMap, dataRows, nameIsMapped]);

  // Schema warnings for CREATE mode
  const schemaWarnings = useMemo(() => {
    if (mode !== 'create') return [];
    const warnings = [];
    if (!nameIsMapped) {
      warnings.push({ id: 'name_required', level: 'error', msg: 'Name column must be mapped in Create mode.' });
    }
    if (!categoryIsMapped) {
      warnings.push({ id: 'category_unmapped', level: 'confirm', msg: 'Category is not mapped — all rows will have empty category.' });
    }
    if (suspiciousNameMapping) {
      warnings.push({ id: 'suspicious_name', level: 'confirm', msg: 'The Name column has very few unique values — this may be a Category column mapped as Name.' });
    }
    return warnings;
  }, [mode, nameIsMapped, categoryIsMapped, suspiciousNameMapping]);

  const hasBlockingErrors = schemaWarnings.some(w => w.level === 'error');
  const unackedConfirms = schemaWarnings.filter(w => w.level === 'confirm').filter(w => {
    if (w.id === 'category_unmapped') return emptyCategoryAck !== 'EMPTY';
    if (w.id === 'suspicious_name') return !suspiciousNameAck;
    return true;
  });

  // ── Mapped + validated rows ──
  const mappedEntries = Object.entries(columnMap);
  const hasMappedFields = mappedEntries.length > 0;

  const canProceedToPreview = hasMappedFields && !hasBlockingErrors && unackedConfirms.length === 0;

  const mappedData = useMemo(() => {
    if (!hasMappedFields) return [];
    return dataRows.map((row, rowIdx) => {
      const item = {};
      const errors = {};

      mappedEntries.forEach(([ci, field]) => {
        const raw = row[Number(ci)] || '';
        const err = validateCell(raw, field);
        if (err) errors[field] = err;

        if (['qty', 'revenue_unit', 'cost_unit'].includes(field)) {
          item[field] = parseNum(raw);
        } else if (['revenue_currency', 'cost_currency'].includes(field)) {
          item[field] = raw ? raw.toUpperCase() : null;
        } else {
          item[field] = raw || null;
        }
      });

      if (mode === 'create' && !item.name) errors._row = 'Name required';

      const qty = item.qty ?? 1;
      const revUnit = item.revenue_unit ?? 0;
      const costUnit = item.cost_unit ?? 0;
      const revCur = item.revenue_currency || coreCurrency;
      const costCur = item.cost_currency || coreCurrency;
      const revBase = qty * toChfPreview(revUnit, revCur, fxRates, coreCurrency);
      const costBase = qty * toChfPreview(costUnit, costCur, fxRates, coreCurrency);
      const marginBase = revBase - costBase;
      const marginPct = revBase > 0 ? (marginBase / revBase * 100) : 0;

      return { rowIdx, item, errors, hasErrors: Object.keys(errors).length > 0, preview: { revChf: revBase, costChf: costBase, marginChf: marginBase, marginPct } };
    });
  }, [dataRows, mappedEntries, mode, fxRates, hasMappedFields, coreCurrency]);

  const summary = useMemo(() => {
    const valid = mappedData.filter(r => !r.hasErrors);
    const totalRev = valid.reduce((s, r) => s + r.preview.revChf, 0);
    const totalCost = valid.reduce((s, r) => s + r.preview.costChf, 0);
    const marginPct = totalRev > 0 ? ((totalRev - totalCost) / totalRev * 100) : 0;
    return { total: dataRows.length, valid: valid.length, errors: mappedData.length - valid.length, totalRev, totalCost, marginPct };
  }, [mappedData, dataRows.length]);

  const fillOverflow = mode === 'fill' && dataRows.length > existingItemCount;
  const canImport = summary.valid > 0 && hasMappedFields && !hasBlockingErrors;

  const handleImport = async () => {
    const items = mappedData.filter(r => !r.hasErrors).map(r => r.item);
    await onImport(mode, items, 0);
    handleOpenChange(false);
  };

  // ─── Render ─────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl p-0 gap-0" data-testid="paste-import-dialog">
        {/* Title */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <ClipboardPaste className="h-4 w-4" />
            Paste Assistant
            {step !== 'paste' && (
              <span className="text-xs font-normal text-gray-400 ml-2">
                {step === 'map' ? 'Map Columns' : 'Preview & Import'}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* ── STEP: PASTE ── */}
        {step === 'paste' && (
          <div className="p-5 space-y-3" data-testid="paste-step">
            <p className="text-xs text-gray-500">
              Paste tabular data from Excel or Google Sheets. Tabs or commas as separators.
            </p>
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder={"name\tqty\trevenue_unit\tcost_unit\nSpeaker System\t4\t1200\t800\nLighting Rig\t2\t3500\t2100"}
              className="w-full h-48 border border-gray-200 rounded-md p-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-gray-200 placeholder:text-gray-300"
              data-testid="paste-textarea"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} className="h-8 text-xs" data-testid="paste-cancel-btn">Cancel</Button>
              <Button
                size="sm"
                onClick={handleParse}
                disabled={!rawText.trim()}
                className="h-8 text-xs bg-gray-900 text-white hover:bg-gray-800 gap-1.5"
                data-testid="paste-parse-btn"
              >
                Parse <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP: MAP ── */}
        {step === 'map' && (
          <div className="space-y-0" data-testid="map-step">
            {/* Column dropdowns */}
            <div className="px-5 pt-4 pb-3">
              <p className="text-xs text-gray-500 mb-3">
                {hasHeaders ? 'Headers detected — auto-mapped. Adjust if needed.' : 'No headers detected — map each column manually.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {headerLabels.map((label, ci) => (
                  <div key={ci} className="flex flex-col gap-1 min-w-[110px]">
                    <span className="text-[10px] font-medium text-gray-400 uppercase truncate" title={label}>{label}</span>
                    <Select value={getMapped(ci)} onValueChange={(v) => setMapping(ci, v)}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`map-col-${ci}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TARGET_FIELDS.map(f => (
                          <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            {/* Mode toggle */}
            <div className="px-5 pb-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-100">
                <span className="text-xs font-medium text-gray-500">Mode:</span>
                <button
                  onClick={() => { setMode('create'); setEmptyCategoryAck(false); setSuspiciousNameAck(false); }}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${mode === 'create' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                  data-testid="mode-create-btn"
                >
                  Create new rows
                </button>
                <button
                  onClick={() => { setMode('fill'); setEmptyCategoryAck(false); setSuspiciousNameAck(false); }}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${mode === 'fill' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                  data-testid="mode-fill-btn"
                >
                  Fill existing rows
                </button>
                {mode === 'fill' && (
                  <span className="text-[10px] text-gray-400">{existingItemCount} existing row{existingItemCount !== 1 ? 's' : ''}</span>
                )}
              </div>
              {fillOverflow && (
                <p className="text-[10px] text-amber-600 mt-1">
                  {dataRows.length} pasted rows but only {existingItemCount} existing — extra rows will be skipped.
                </p>
              )}
            </div>

            {/* Mini data preview */}
            <div className="px-5 pb-3">
              <ScrollArea className="max-h-36 border border-gray-100 rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/60 hover:bg-gray-50/60">
                      <TableHead className="text-[10px] w-8 pl-3">#</TableHead>
                      {headerLabels.map((h, i) => (
                        <TableHead key={i} className="text-[10px]">
                          {getMapped(i) !== '_skip'
                            ? <span className="text-blue-600">{TARGET_FIELDS.find(f => f.value === getMapped(i))?.label}</span>
                            : <span className="text-gray-300 line-through">{h}</span>}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dataRows.slice(0, 4).map((row, ri) => (
                      <TableRow key={ri}>
                        <TableCell className="text-[10px] text-gray-300 py-1 pl-3">{ri + 1}</TableCell>
                        {row.map((cell, ci) => (
                          <TableCell key={ci} className={`text-[11px] py-1 ${getMapped(ci) === '_skip' ? 'text-gray-300' : ''}`}>
                            {cell || '\u00A0'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {dataRows.length > 4 && (
                      <TableRow>
                        <TableCell colSpan={colCount + 1} className="text-[10px] text-gray-400 text-center py-1">
                          +{dataRows.length - 4} more rows
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>

            {/* Schema validation warnings */}
            {schemaWarnings.length > 0 && (
              <div className="px-5 pb-3 space-y-2" data-testid="schema-warnings">
                {schemaWarnings.map(w => (
                  <div
                    key={w.id}
                    className={`flex items-start gap-2.5 p-2.5 rounded-md border text-xs ${
                      w.level === 'error'
                        ? 'bg-red-50 border-red-200 text-red-700'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}
                    data-testid={`schema-warning-${w.id}`}
                  >
                    {w.level === 'error'
                      ? <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      : <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    }
                    <div className="flex-1">
                      <p>{w.msg}</p>
                      {w.level === 'confirm' && w.id === 'category_unmapped' && (
                        <div className="mt-2">
                          <p className="text-[10px] mb-1.5 font-medium">Type <span className="font-mono bg-amber-100 px-1 rounded">EMPTY</span> to confirm:</p>
                          <input
                            type="text"
                            value={emptyCategoryAck}
                            onChange={(e) => setEmptyCategoryAck(e.target.value.toUpperCase())}
                            placeholder="EMPTY"
                            className="w-32 px-2 py-1 text-xs border border-amber-300 rounded bg-white font-mono tracking-wider focus:outline-none focus:ring-1 focus:ring-amber-400"
                            data-testid="ack-category_unmapped"
                            autoComplete="off"
                            spellCheck="false"
                          />
                        </div>
                      )}
                      {w.level === 'confirm' && w.id === 'suspicious_name' && (
                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                          <Checkbox
                            checked={suspiciousNameAck}
                            onCheckedChange={(v) => setSuspiciousNameAck(!!v)}
                            data-testid="ack-suspicious_name"
                          />
                          <span className="font-medium">I confirm: this column is correct for Name</span>
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Nav */}
            <div className="flex justify-between items-center px-5 py-3 border-t border-gray-100">
              <Button variant="outline" size="sm" onClick={() => setStep('paste')} className="h-8 text-xs gap-1.5" data-testid="map-back-btn">
                <ArrowLeft className="h-3 w-3" /> Back
              </Button>
              <Button
                size="sm"
                onClick={() => setStep('preview')}
                disabled={!canProceedToPreview}
                className="h-8 text-xs bg-gray-900 text-white hover:bg-gray-800 gap-1.5"
                data-testid="map-preview-btn"
              >
                Preview <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP: PREVIEW ── */}
        {step === 'preview' && (
          <div className="space-y-0" data-testid="preview-step">
            {/* Summary bar */}
            <div className="px-5 pt-4 pb-3 flex items-center gap-5 flex-wrap">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <Stat label="Rows" value={summary.valid} sub={summary.errors > 0 ? `${summary.errors} err` : null} subColor="text-red-500" />
                {mode === 'create' && (
                  <>
                    <Stat label="Revenue" value={formatNumber(summary.totalRev)} unit="CHF" mono />
                    <Stat label="Cost" value={formatNumber(summary.totalCost)} mono />
                    <Stat label="Margin" value={formatPercent(summary.marginPct)} color={summary.marginPct >= 30 ? 'text-green-600' : summary.marginPct >= 15 ? 'text-yellow-600' : 'text-red-600'} mono />
                  </>
                )}
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${mode === 'create' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
                {mode === 'create' ? 'CREATE' : 'FILL'}
              </span>
            </div>

            {/* Full preview table */}
            <ScrollArea className="max-h-[340px] border-t border-gray-100">
              <Table data-testid="preview-table">
                <TableHeader>
                  <TableRow className="bg-gray-50/60 hover:bg-gray-50/60">
                    <TableHead className="text-[10px] w-8 pl-5">#</TableHead>
                    {mappedEntries.map(([ci, field]) => (
                      <TableHead key={ci} className="text-[10px] font-semibold uppercase tracking-wider">
                        {TARGET_FIELDS.find(f => f.value === field)?.label}
                      </TableHead>
                    ))}
                    {mode === 'create' && (
                      <>
                        <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">Rev CHF</TableHead>
                        <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">Cost CHF</TableHead>
                        <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">Margin</TableHead>
                      </>
                    )}
                    <TableHead className="w-6" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappedData.map((row) => (
                    <TableRow key={row.rowIdx} className={row.hasErrors ? 'bg-red-50/50' : ''} data-testid={`preview-row-${row.rowIdx}`}>
                      <TableCell className="text-[10px] text-gray-300 py-1.5 pl-5">{row.rowIdx + 1}</TableCell>
                      {mappedEntries.map(([ci, field]) => {
                        const val = row.item[field];
                        const err = row.errors[field];
                        return (
                          <TableCell key={ci} className="py-1.5">
                            <span
                              className={`text-xs ${err ? 'text-red-600 underline decoration-wavy decoration-red-300' : ''}`}
                              title={err || undefined}
                            >
                              {val !== null && val !== undefined ? String(val) : '\u2014'}
                            </span>
                          </TableCell>
                        );
                      })}
                      {mode === 'create' && (
                        <>
                          <TableCell className="text-right py-1.5 font-mono text-xs tabular-nums">{formatNumber(row.preview.revChf)}</TableCell>
                          <TableCell className="text-right py-1.5 font-mono text-xs tabular-nums text-gray-500">{formatNumber(row.preview.costChf)}</TableCell>
                          <TableCell className="text-right py-1.5 font-mono text-xs tabular-nums">
                            <span className={row.preview.marginPct >= 30 ? 'text-green-600' : row.preview.marginPct >= 15 ? 'text-yellow-600' : 'text-red-600'}>
                              {formatPercent(row.preview.marginPct)}
                            </span>
                          </TableCell>
                        </>
                      )}
                      <TableCell className="py-1.5 pr-3">
                        {row.hasErrors && <AlertCircle className="h-3 w-3 text-red-400" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Footer actions */}
            <div className="flex justify-between items-center px-5 py-3 border-t border-gray-100">
              <Button variant="outline" size="sm" onClick={() => setStep('map')} className="h-8 text-xs gap-1.5" data-testid="preview-back-btn">
                <ArrowLeft className="h-3 w-3" /> Back
              </Button>
              <Button
                size="sm"
                onClick={handleImport}
                disabled={!canImport || loading}
                className="h-8 text-xs bg-gray-900 text-white hover:bg-gray-800 gap-1.5"
                data-testid="import-confirm-btn"
              >
                <Check className="h-3 w-3" />
                Import {summary.valid} row{summary.valid !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Tiny stat block ──────────────────────────────────────

function Stat({ label, value, unit, sub, subColor, color, mono }) {
  return (
    <div className="text-xs whitespace-nowrap">
      <span className="text-gray-400">{label}: </span>
      <span className={`font-semibold ${color || ''} ${mono ? 'font-mono' : ''}`}>{value}</span>
      {unit && <span className="text-gray-400 text-[10px] ml-0.5">{unit}</span>}
      {sub && <span className={`ml-1 ${subColor || 'text-gray-400'}`}>({sub})</span>}
    </div>
  );
}
